import React from 'react'
import { Modal, Form, Input, InputNumber, Select, Switch, Button, Space, Divider, Checkbox, Row, Col } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import './createPlanModal.scss'
import { useDispatch, useSelector } from 'react-redux'
import { appDispatch, RootState } from '../../../app/store'
import { createPlanFeature, fetchFeaturesListData, updatePlanFeature, deletePlanFeature, resetCreatePlanFeature, resetUpdatePlanFeature, resetDeletePlanFeature } from '../../../features/superAdmin/membershipManagement/membershipManagementSlice'
import { toast } from 'react-toastify'

interface CreatePlanModalProps {
  open: boolean
  onCancel: () => void
  onSubmit: (values: CreatePlanPayload) => void
  loading?: boolean
  editingPlan?: MembershipPlan | null,
  featuresListData:any;
}

interface CreatePlanPayload {
  name: string
  price: string
  duration_days: number
  description: string
  currency: string
  billing_cycle: string
  network_type_id: number
  is_free_trial_subscription_plan: boolean
  is_active: boolean
  feature_ids: number[]
}

interface MembershipPlan {
  id: number
  name: string
  price: string
  currency: string
  billing_cycle: string
  is_free_trial_subscription_plan: boolean
  active_features: Array<{id: number, name: string}>
  inactive_features: Array<{id: number, name: string}>
}

const { Option } = Select
const { TextArea } = Input

export const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
  open,
  onCancel,
  onSubmit,
  loading = false,
  editingPlan = null,
  featuresListData
}) => {
  const dispatch = useDispatch<appDispatch>()
  const [form] = Form.useForm()
  const [isAddFeatureModalOpen, setIsAddFeatureModalOpen] = React.useState(false)
  const [isEditFeatureModalOpen, setIsEditFeatureModalOpen] = React.useState(false)
  const [editingFeature, setEditingFeature] = React.useState<{value: number, label: string} | null>(null)
  const [customFeatures, setCustomFeatures] = React.useState<Array<{value: number, label: string}>>([])
  const [newFeatureForm] = Form.useForm()
  const [editFeatureForm] = Form.useForm()

  // Slice selectors for feature CRUD
  const { isSuccess: isCreateFeatureSuccess, isError: isCreateFeatureError, message: createFeatureMessage } = useSelector((s: RootState) => s.membershipManagement.createPlanFeature)
  const { isSuccess: isUpdateFeatureSuccess, isError: isUpdateFeatureError, message: updateFeatureMessage } = useSelector((s: RootState) => s.membershipManagement.updatePlanFeature)
  const { isSuccess: isDeleteFeatureSuccess, isError: isDeleteFeatureError, message: deleteFeatureMessage } = useSelector((s: RootState) => s.membershipManagement.deletePlanFeature)

  // Toast on feature create
  React.useEffect(() => {
    if (isCreateFeatureSuccess) {
      toast.success(createFeatureMessage || 'Feature created successfully')
      dispatch(resetCreatePlanFeature())
    }
    if (isCreateFeatureError) {
      toast.error(createFeatureMessage || 'Failed to create feature')
      dispatch(resetCreatePlanFeature())
    }
  }, [isCreateFeatureSuccess, isCreateFeatureError, createFeatureMessage, dispatch])

  // Toast on feature update
  React.useEffect(() => {
    if (isUpdateFeatureSuccess) {
      toast.success(updateFeatureMessage || 'Feature updated successfully')
      dispatch(resetUpdatePlanFeature())
    }
    if (isUpdateFeatureError) {
      toast.error(updateFeatureMessage || 'Failed to update feature')
      dispatch(resetUpdatePlanFeature())
    }
  }, [isUpdateFeatureSuccess, isUpdateFeatureError, updateFeatureMessage, dispatch])

  // Toast on feature delete
  React.useEffect(() => {
    if (isDeleteFeatureSuccess) {
      toast.success(deleteFeatureMessage || 'Feature removed successfully')
      dispatch(resetDeletePlanFeature())
    }
    if (isDeleteFeatureError) {
      toast.error(deleteFeatureMessage || 'Failed to remove feature')
      dispatch(resetDeletePlanFeature())
    }
  }, [isDeleteFeatureSuccess, isDeleteFeatureError, deleteFeatureMessage, dispatch])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const isFreeTrial = !!values?.is_free_trial_subscription_plan
      const payload = isFreeTrial ? { ...values, price: '0' } : values
      onSubmit(payload)
    } catch (error) {
      console.error('Form validation failed:', error)
    }
  }

  const handleCancel = () => {
    // Reset form with default values
    form.setFieldsValue({
      currency: 'USD',
      billing_cycle: 'month',
      network_type_id: 3,
      is_free_trial_subscription_plan: false,
      is_active: true,
      feature_ids: [],
      name: '',
      price: '',
      description: '',
    })
    onCancel()
  }

  // Initialize form with editing plan data
  React.useEffect(() => {
    if (editingPlan && open) {
      const activeFeatureIds = (editingPlan as any).active_features?.map((f: any) => f.id) || []
      
      form.setFieldsValue({
        name: editingPlan.name,
        price: (editingPlan as any).price?.toString()?.replace('$', '') ?? '',
        currency: (editingPlan as any).currency,
        billing_cycle: (editingPlan as any).billing_cycle,
        is_free_trial_subscription_plan: (editingPlan as any).is_free_trial_subscription_plan,
        is_active: (editingPlan as any).is_active ?? true,
        feature_ids: activeFeatureIds,
        description: (editingPlan as any).description ?? `${editingPlan.name} plan`,
        network_type_id: (editingPlan as any).network_type_id ?? 3,
      })
    } else if (open) {
      // Reset form for create mode with default values
      form.setFieldsValue({
        currency: 'USD',
        billing_cycle: 'month',
        network_type_id: 3,
        is_free_trial_subscription_plan: false,
        is_active: true,
        feature_ids: [],
        name: '',
        price: '',
        description: '',
      })
    }
  }, [editingPlan, open, form])

  const isEditMode = !!editingPlan
  const modalTitle = isEditMode ? 'Edit Membership Plan' : 'Create New Membership Plan'
  const submitButtonText = isEditMode ? 'Update Plan' : 'Create Plan'
  const submitButtonIcon = isEditMode ? <EditOutlined /> : <PlusOutlined />

  const handleAddFeature = async () => {
    try {
      const values = await newFeatureForm.validateFields()
      const networkTypeId = selectedNetworkTypeId || 3

      // Create feature via API
      await dispatch(createPlanFeature({
        name: values.featureName,
        network_type_id: networkTypeId,
      } as any)).unwrap()

      // Refresh features list for the selected network type
      dispatch(fetchFeaturesListData({ networkTypeId }))

      newFeatureForm.resetFields()
      setIsAddFeatureModalOpen(false)
    } catch (error: any) {
      console.error('Feature validation/creation failed:', error)
      // Toast handled by selector effect
    }
  }

  const handleRemoveFeature = async (featureValue: number) => {
    try {
      const networkTypeId = selectedNetworkTypeId || 3
      const apiList: any[] = Array.isArray(featuresListData) ? featuresListData : []
      const existsInApi = apiList.some((f: any) => f?.id === featureValue)

      if (existsInApi) {
        // Delete via API
        await dispatch(deletePlanFeature({ ids: [featureValue] } as any)).unwrap()
        // Refresh features list
        dispatch(fetchFeaturesListData({ networkTypeId }))
      } else {
        // Remove from custom features if it exists there
        setCustomFeatures(prev => prev.filter(f => f.value !== featureValue))
      }
      
      // Also remove from form values if it was selected
      const currentFeatureIds = form.getFieldValue('feature_ids') || []
      form.setFieldsValue({
        feature_ids: currentFeatureIds.filter((id: number) => id !== featureValue)
      })
    } catch (error: any) {
      console.error('Feature deletion failed:', error)
      // Toast handled by selector effect
    }
  }

  const showRemoveFeatureConfirm = (featureValue: number, featureLabel: string) => {
    Modal.confirm({
      title: 'Remove feature?',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to remove feature "${featureLabel}"?`,
      centered: true,
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: { danger: true },
      onOk: () => handleRemoveFeature(featureValue)
    })
  }

  const handleEditFeature = (feature: {value: number, label: string}) => {
    setEditingFeature(feature)
    editFeatureForm.setFieldsValue({
      featureName: feature.label
    })
    setIsEditFeatureModalOpen(true)
  }

  const handleUpdateFeature = async () => {
    try {
      const values = await editFeatureForm.validateFields()
      if (editingFeature) {
        const featureId = editingFeature.value
        const networkTypeId = selectedNetworkTypeId || 3
        const apiList: any[] = Array.isArray(featuresListData) ? featuresListData : []
        const existsInApi = apiList.some((f: any) => f?.id === featureId)

        if (existsInApi) {
          // Update feature via API
          await dispatch(updatePlanFeature({
            feature_id: featureId,
            name: values.featureName,
            network_type_id: networkTypeId,
            is_active: true,
          } as any)).unwrap()

          // Refresh features list
          dispatch(fetchFeaturesListData({ networkTypeId }))
        } else {
          // Update custom feature if it exists only locally
          setCustomFeatures(prev => 
            prev.map(f => 
              f.value === featureId 
                ? { ...f, label: values.featureName }
                : f
            )
          )
        }

        editFeatureForm.resetFields()
        setIsEditFeatureModalOpen(false)
        setEditingFeature(null)
      }
    } catch (error: any) {
      console.error('Feature validation/update failed:', error)
      // Toast handled by selector effect
    }
  }

  const currencyOptions = [
    { value: 'USD', label: 'USD' },
    { value: 'EUR', label: 'EUR' },
    { value: 'GBP', label: 'GBP' },
    { value: 'CAD', label: 'CAD' },
  ]

  const billingCycleOptions = [
    // { value: 'day', label: 'Daily' },
    // { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'year', label: 'Yearly' },
  ]

  const networkTypeOptions = [
    { value: 1, label: 'Network Type 1' },
    { value: 2, label: 'Network Type 2' },
    { value: 3, label: 'Network Type 3' },
    { value: 4, label: 'Network Type 4' },
  ]

  // Removed static feature options; list now comes from API

  // Combine default features with custom features
  const selectedNetworkTypeId = Form.useWatch('network_type_id', form)
  const apiFeatures = React.useMemo(() => {
    const list = Array.isArray(featuresListData) ? featuresListData : []
    return list
      .filter((f: any) => f?.is_active && (!selectedNetworkTypeId || f?.network_type_id === selectedNetworkTypeId))
      .map((f: any) => ({ value: f.id, label: f.name }))
  }, [featuresListData, selectedNetworkTypeId])
  const allFeatures = [...apiFeatures, ...customFeatures]
  const isFreeTrialPlan = Form.useWatch('is_free_trial_subscription_plan', form)

  React.useEffect(() => {
    if (isFreeTrialPlan) {
      form.setFieldsValue({ price: '' })
    }
  }, [isFreeTrialPlan, form])

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={modalTitle}
      width={600}
      className="create-plan-modal"
      destroyOnClose
      centered
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          icon={submitButtonIcon}
        >
          {submitButtonText}
        </Button>,
      ]}
      bodyStyle={{ 
        maxHeight: '60vh', 
        overflowY: 'auto',
        padding: '16px 24px'
      }}
      closable={false}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          currency: 'USD',
          billing_cycle: 'month',
          network_type_id: 3,
          is_free_trial_subscription_plan: false,
          is_active: true,
          feature_ids: [],
        }}
        className="create-plan-form"
      >
        <div className="form-section">
            <Form.Item
              name="name"
              label="Plan Name"
              rules={[{ required: true, message: 'Please enter plan name' }, { max: 50, message: 'Plan name must be 50 characters or fewer' }]}
            >
              <Input placeholder="Enter plan name" maxLength={50} showCount />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please enter plan description' }]}
            >
              <TextArea
                rows={3}
                placeholder="Enter plan description"
                maxLength={500}
                showCount
              />
            </Form.Item>
        </div>


        <div className="form-section">
            <div className="form-row">
              <Form.Item
                name="price"
                label="Price"
                rules={isFreeTrialPlan ? [] : [{ required: true, message: 'Please enter price' }]}
                style={{ flex: 1, marginRight: 16 }}
              >
                <Input placeholder="0.00" disabled={!!isFreeTrialPlan} />
              </Form.Item>

              <Form.Item
                name="currency"
                label="Currency"
                rules={isFreeTrialPlan ? [] : [{ required: true, message: 'Please select currency' }]}
                style={{ flex: 1 }}
              >
                <Select placeholder="Select currency" disabled={!!isFreeTrialPlan}>
                  {currencyOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div className="form-row">
              <Form.Item
                name="billing_cycle"
                label="Billing Cycle"
                rules={[{ required: true, message: 'Please select billing cycle' }]}
                style={{ flex: 1 }}
              >
                <Select placeholder="Select billing cycle">
                  {billingCycleOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
        </div>



        <div className="form-section">
            <div className="form-row">
              <Form.Item
                name="is_free_trial_subscription_plan"
                label="Free Trial Plan"
                valuePropName="checked"
                style={{ flex: 1, marginRight: 16 }}
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="is_active"
                label="Active Plan"
                valuePropName="checked"
                style={{ flex: 1 }}
              >
                <Switch />
              </Form.Item>
            </div>
            {/* <Form.Item
              name="network_type_id"
              label="Network Type"
              rules={[{ required: true, message: 'Please select network type' }]}
            >
              <Select placeholder="Select network type">
                {networkTypeOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item> */}
        </div>



                 <div className="form-section">
           <div className="section-title">
             Features
             <Button
               type="dashed"
               size="small"
               icon={<PlusOutlined />}
               onClick={() => setIsAddFeatureModalOpen(true)}
               style={{ marginLeft: 12 }}
               className="add-feature-btn"
             >
               Add Feature
             </Button>
           </div>
           <Form.Item
             name="feature_ids"
             label="Select Features"
             rules={[{ required: true, message: 'Please select at least one feature' }]}
           >
             <Checkbox.Group style={{ width: '100%' }}>
               <Row gutter={[16, 8]}>
                 {allFeatures.map(option => (
                   <Col span={24} key={option.value}>
                     <div className="feature-item-row">
                       <div className="feature-checkbox-wrapper">
                         <Checkbox value={option.value} className="feature-checkbox">
                           {option.label}
                         </Checkbox>
                       </div>
                       <div className="feature-actions">
                         <Button
                           type="text"
                           size="small"
                           icon={<EditOutlined />}
                           onClick={() => handleEditFeature(option)}
                           className="edit-feature-btn"
                           title="Edit feature"
                         />
                         <Button
                           type="text"
                           size="small"
                           icon={<DeleteOutlined />}
                           className="remove-feature-btn"
                           title="Remove feature"
                           onClick={() => showRemoveFeatureConfirm(option.value, option.label)}
                         />
                       </div>
                     </div>
                   </Col>
                 ))}
               </Row>
             </Checkbox.Group>
           </Form.Item>
         </div>
      </Form>

      {/* Add Feature Modal */}
      <Modal
        open={isAddFeatureModalOpen}
        onCancel={() => {
          setIsAddFeatureModalOpen(false)
          newFeatureForm.resetFields()
        }}
        title="Add New Feature"
        width={400}
        className="add-feature-modal"
        destroyOnClose
        centered
        footer={[
          <Button key="cancel" onClick={() => {
            setIsAddFeatureModalOpen(false)
            newFeatureForm.resetFields()
          }}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleAddFeature}
            icon={<PlusOutlined />}
          >
            Add Feature
          </Button>,
        ]}
        closable={false}
      >
        <Form
          form={newFeatureForm}
          layout="vertical"
          className="add-feature-form"
        >
          <Form.Item
            name="featureName"
            label="Feature Name"
            rules={[
              { required: true, message: 'Please enter feature name' },
              { min: 3, message: 'Feature name must be at least 3 characters' },
              { max: 100, message: 'Feature name must be less than 100 characters' }
            ]}
          >
            <Input 
              placeholder="Enter feature name" 
              maxLength={100}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Feature Modal */}
      <Modal
        open={isEditFeatureModalOpen}
        onCancel={() => {
          setIsEditFeatureModalOpen(false)
          setEditingFeature(null)
          editFeatureForm.resetFields()
        }}
        title="Edit Feature"
        width={400}
        className="edit-feature-modal"
        destroyOnClose
        centered
        footer={[
          <Button key="cancel" onClick={() => {
            setIsEditFeatureModalOpen(false)
            setEditingFeature(null)
            editFeatureForm.resetFields()
          }}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleUpdateFeature}
            icon={<EditOutlined />}
          >
            Update Feature
          </Button>,
        ]}
        closable={false}
      >
        <Form
          form={editFeatureForm}
          layout="vertical"
          className="edit-feature-form"
        >
          <Form.Item
            name="featureName"
            label="Feature Name"
            rules={[
              { required: true, message: 'Please enter feature name' },
              { min: 3, message: 'Feature name must be at least 3 characters' },
              { max: 100, message: 'Feature name must be less than 100 characters' }
            ]}
          >
            <Input 
              placeholder="Enter feature name" 
              maxLength={100}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  )
}
